import { useProductAdsRendered, useUpdateProductAdViewed } from '@/scopes/product-ads/domain/hooks';
import type { ProductAd } from '@/scopes/product-ads/domain/types';
import { buildImgSrc } from '@/helpers/image-url';
import { useIntl } from '@/intl';
import { useOnScreen } from '@/scopes/common/components/hooks/useOnScreen';
import { Icon, Image, tk, Typography, verticalGap } from '@sunwebgroup/ui-components';
import { useEffect, useRef, useState } from 'react';
import { styled } from 'styled-components';
import { createProductAdItemClickedEvent } from '@/scopes/product-ads/domain/analytics';
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks';

interface ProductAdProps {
  data: ProductAd;
  inView: boolean;
  componentType: string;
}

const Wrapper = styled.div`
  position: relative;
  width: 280px;
  box-shadow: ${tk('shadow_md_bottom')};
  border-radius: ${tk('radius_large')};
  background-color: ${tk('color_semantic_bg')};
  height: 100%;

  @media ${tk('mq_desktop_sm')} {
    width: 320px;
  }
`;

const ProductImage = styled(Image.Root)`
  object-fit: cover;
  border-radius: ${tk('radius_large')} ${tk('radius_large')} 0px 0px;
`;

const Footer = styled.div`
  padding: ${tk('spacing_md')};
  display: flex;
  flex-direction: column;

  ${verticalGap('spacing_xs')}
`;

const Heading = styled(Typography)`
  color: ${tk('color_semantic_text')};
`;

const SubHeading = styled(Typography)`
  display: flex;
  align-items: end;
  color: ${tk('color_semantic_text_strong')};
`;

const Link = styled.a`
  // Link styles are needed to override the MVC styles
  // TODO: remove once page is fully migrated to React
  text-decoration: none;

  // ------------------------------

  &:hover {
    ${ProductImage} img {
      filter: brightness(0.85);
      transition: all 0.3s ease-in-out;
    }
  }

  &:not(:hover) {
    ${ProductImage} img {
      transition: all 0.3s ease-in-out;
    }
  }
`;

export function ProductAd({ data, inView, componentType }: ProductAdProps) {
  const intl = useIntl();
  const ref = useRef<HTMLDivElement>(null);
  const isOnScreen = useOnScreen(ref, { threshold: 0.5 });
  const [isViewed, setIsViewed] = useState(false);
  const { sendEvent } = useAnalytics();

  //we notify the productAd is rendered
  useProductAdsRendered(data.rendered);

  const { update } = useUpdateProductAdViewed(data.viewed);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-empty-function -- setTimeout needs a function as first argument
    let timer: ReturnType<typeof setTimeout> = setTimeout(() => {}, 0);
    if (isOnScreen && !isViewed) {
      timer = setTimeout(async () => {
        if (isOnScreen) {
          await update();
          setIsViewed(true);
        }
      }, 1000);
    } else {
      clearTimeout(timer);
    }

    return () => clearTimeout(timer);
  }, [data.viewed, isOnScreen, isViewed, update]);

  const handleOnClick = () => {
    localStorage.setItem('C_URL', data.conversionUrl ?? '');
    localStorage.setItem('acco_ID', data.accoId);
    sendEvent(createProductAdItemClickedEvent({ ads: [data], type: componentType }));
  };

  return (
    <Wrapper ref={ref} className={`FID_${data.flightId}`}>
      <Link href={data.clicked} target="_blank" rel="noreferrer" onClick={handleOnClick}>
        <ProductImage
          defaultSource={{
            src: buildImgSrc(data.image, 280, 215),
            width: 280,
            height: 215,
          }}
          loaderSource={{
            src: buildImgSrc(data.image, 20),
          }}
          sources={[
            {
              src: buildImgSrc(data.image, 320, 215),
              width: 320,
              height: 215,
              breakpoint: 'breakpoint_desktop_sm',
            },
          ]}
          alternativeText={data.heading}
          startImmediateLoad={inView}
        >
          <Image.Error>
            <Icon glyph="IconNoImage" size="lg" />
            <Typography $variant="body-md-bold">{intl.formatMessage({ id: 'roomtypes_image_error' })}</Typography>
          </Image.Error>
        </ProductImage>
        <Footer>
          <Heading $variant="heading-md">{data.heading}</Heading>
          <SubHeading $variant="body-xs">
            <Icon glyph="IconPin" size="sm" />
            {data.subHeading}
          </SubHeading>
        </Footer>
      </Link>
    </Wrapper>
  );
}
